import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  buildStepHref,
  getGuidedTourTopic,
  locationMatchesStep,
  type GuidedTourId,
  type GuidedTourStepDef,
} from './definitions';

type GuidedTourContextValue = {
  startTour: (id: GuidedTourId) => void;
  stopTour: () => void;
  isRunning: boolean;
  activeTourId: GuidedTourId | null;
};

const GuidedTourContext = createContext<GuidedTourContextValue | null>(null);

function toJoyrideSteps(steps: GuidedTourStepDef[]): Step[] {
  return steps.map((s) => ({
    target: s.target,
    title: s.title,
    content: <span style={{ whiteSpace: 'pre-line' }}>{s.content}</span>,
    placement: s.placement ?? 'auto',
    disableBeacon: true,
    spotlightPadding: s.spotlightPadding ?? 8,
    isFixed: s.joyrideFixed ?? false,
  }));
}

export function GuidedTourProvider({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTourId, setActiveTourId] = useState<GuidedTourId | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);
  const pendingStepIndexRef = useRef<number | null>(null);

  const topic = activeTourId ? getGuidedTourTopic(activeTourId) : undefined;
  const steps = topic?.steps ?? [];

  const joyrideSteps = useMemo(() => toJoyrideSteps(steps), [steps]);

  const resetInternal = useCallback(() => {
    setActiveTourId(null);
    setStepIndex(0);
    setRun(false);
    pendingStepIndexRef.current = null;
  }, []);

  const stopTour = useCallback(() => {
    resetInternal();
  }, [resetInternal]);

  const startTour = useCallback(
    (id: GuidedTourId) => {
      const t = getGuidedTourTopic(id);
      if (!t || t.steps.length === 0) return;

      pendingStepIndexRef.current = null;
      setActiveTourId(id);
      setStepIndex(0);

      const first = t.steps[0];
      if (locationMatchesStep(location.pathname, location.search, first)) {
        setRun(true);
      } else {
        pendingStepIndexRef.current = 0;
        navigate(buildStepHref(first.path, first.search));
        setRun(false);
      }
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    const pending = pendingStepIndexRef.current;
    if (pending === null || !activeTourId || !topic) return;
    const step = topic.steps[pending];
    if (!step) return;

    if (locationMatchesStep(location.pathname, location.search, step)) {
      pendingStepIndexRef.current = null;
      setStepIndex(pending);
      const t = window.setTimeout(() => setRun(true), 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [location.pathname, location.search, activeTourId, topic]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        resetInternal();
        return;
      }

      if (type === EVENTS.TARGET_NOT_FOUND) {
        return;
      }

      if (type !== EVENTS.STEP_AFTER) return;

      if (action === ACTIONS.NEXT) {
        const nextIndex = index + 1;
        if (nextIndex >= steps.length) {
          resetInternal();
          return;
        }
        const nextDef = steps[nextIndex];
        if (!locationMatchesStep(location.pathname, location.search, nextDef)) {
          pendingStepIndexRef.current = nextIndex;
          setRun(false);
          navigate(buildStepHref(nextDef.path, nextDef.search));
        } else {
          setStepIndex(nextIndex);
        }
      }

      if (action === ACTIONS.PREV) {
        const prevIndex = index - 1;
        if (prevIndex < 0) return;
        const prevDef = steps[prevIndex];
        if (!locationMatchesStep(location.pathname, location.search, prevDef)) {
          pendingStepIndexRef.current = prevIndex;
          setRun(false);
          navigate(buildStepHref(prevDef.path, prevDef.search));
        } else {
          setStepIndex(prevIndex);
        }
      }
    },
    [steps, location.pathname, location.search, navigate, resetInternal],
  );

  const value = useMemo(
    () => ({
      startTour,
      stopTour,
      isRunning: Boolean(activeTourId && run),
      activeTourId,
    }),
    [startTour, stopTour, activeTourId, run],
  );

  return (
    <GuidedTourContext.Provider value={value}>
      {children}
      {activeTourId && joyrideSteps.length > 0 && (
        <Joyride
          key={activeTourId}
          steps={joyrideSteps}
          run={run}
          stepIndex={stepIndex}
          continuous
          showProgress
          showSkipButton
          scrollToFirstStep={false}
          disableScrolling
          disableScrollParentFix
          disableOverlayClose
          callback={handleJoyrideCallback}
          locale={{
            back: 'חזור',
            close: 'סגור',
            last: 'סיום',
            next: 'הבא',
            nextLabelWithProgress: 'הבא ({step} מתוך {steps})',
            skip: 'דלג על הסיור',
          }}
          styles={{
            options: {
              zIndex: 10000,
              primaryColor: theme.palette.primary.main,
              textColor: theme.palette.text.primary,
              backgroundColor: theme.palette.background.paper,
              arrowColor: theme.palette.background.paper,
              overlayColor: 'rgba(0, 0, 0, 0.45)',
            },
            tooltip: {
              borderRadius: 8,
              fontFamily: theme.typography.fontFamily,
            },
            buttonNext: {
              fontFamily: theme.typography.fontFamily,
            },
            buttonBack: {
              fontFamily: theme.typography.fontFamily,
            },
            buttonSkip: {
              fontFamily: theme.typography.fontFamily,
            },
          }}
        />
      )}
    </GuidedTourContext.Provider>
  );
}

export function useGuidedTour(): GuidedTourContextValue {
  const ctx = useContext(GuidedTourContext);
  if (!ctx) {
    throw new Error('useGuidedTour must be used within GuidedTourProvider');
  }
  return ctx;
}
